// apps/dashboard/src/router/router.ts
// TanStack Router v1 — code-based route tree.
// Auth guard: redirects unauthenticated users to /login.

import { AppLayout } from '@/components/layout/app-layout';
import { CompactDashboardShell } from '@/components/mobile/CompactDashboardShell';
import { useAuthStore } from '@/stores/auth.store';
import { createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { createElement, lazy } from 'react';

// ── Lazy page chunks (T13 — one JS chunk per route) ───────────────────────────
const AgentsPage = lazy(() =>
  import('@/pages/agents.page').then((m) => ({ default: m.AgentsPage })),
);
const AnalyticsPage = lazy(() =>
  import('@/pages/analytics.page').then((m) => ({ default: m.AnalyticsPage })),
);
const ApiKeysPage = lazy(() =>
  import('@/pages/api-keys.page').then((m) => ({ default: m.ApiKeysPage })),
);
const AuditLogsPage = lazy(() =>
  import('@/pages/audit-logs.page').then((m) => ({ default: m.AuditLogsPage })),
);
const AutomationPage = lazy(() =>
  import('@/pages/automation.page').then((m) => ({ default: m.AutomationPage })),
);
const TagAutomationsPage = lazy(() =>
  import('@/pages/automations.page').then((m) => ({ default: m.TagAutomationsPage })),
);
const BillingPage = lazy(() =>
  import('@/pages/billing.page').then((m) => ({ default: m.BillingPage })),
);
const BotAnalyticsPage = lazy(() =>
  import('@/pages/bot-analytics.page').then((m) => ({ default: m.BotAnalyticsPage })),
);
const CampaignsPage = lazy(() =>
  import('@/pages/campaigns.page').then((m) => ({ default: m.CampaignsPage })),
);
const CheckoutCancelPage = lazy(() =>
  import('@/pages/checkout-cancel.page').then((m) => ({ default: m.CheckoutCancelPage })),
);
const CheckoutSuccessPage = lazy(() =>
  import('@/pages/checkout-success.page').then((m) => ({ default: m.CheckoutSuccessPage })),
);
const ContactsImportPage = lazy(() =>
  import('@/pages/contacts-import.page').then((m) => ({ default: m.ContactsImportPage })),
);
const ContactsPage = lazy(() =>
  import('@/pages/contacts.page').then((m) => ({ default: m.ContactsPage })),
);
const DsarManagementPage = lazy(() =>
  import('@/pages/dsar-management.page').then((m) => ({ default: m.DsarManagementPage })),
);
const FlowEditorPage = lazy(() =>
  import('@/pages/flow-editor.page').then((m) => ({ default: m.FlowEditorPage })),
);
const InboxV2Page = lazy(() =>
  import('@/pages/inbox-v2.page').then((m) => ({ default: m.InboxV2Page })),
);
const PipelinePage = lazy(() =>
  import('@/pages/pipeline.page').then((m) => ({ default: m.default })),
);
const SegmentsPage = lazy(() =>
  import('@/pages/segments.page').then((m) => ({ default: m.default })),
);
const InboxesPage = lazy(() =>
  import('@/pages/inboxes.page').then((m) => ({ default: m.InboxesPage })),
);
const LiveViewPage = lazy(() =>
  import('@/pages/live-view.page').then((m) => ({ default: m.LiveViewPage })),
);
const LoginPage = lazy(() => import('@/pages/login.page').then((m) => ({ default: m.LoginPage })));
const MarketplacePage = lazy(() =>
  import('@/pages/marketplace.page').then((m) => ({ default: m.MarketplacePage })),
);
const MobileDashboardPage = lazy(() =>
  import('@/pages/mobile/dashboard.page').then((m) => ({ default: m.MobileDashboardPage })),
);
const NotificationsPage = lazy(() =>
  import('@/pages/notifications.page').then((m) => ({ default: m.NotificationsPage })),
);
const OnboardingPage = lazy(() =>
  import('@/pages/onboarding.page').then((m) => ({ default: m.OnboardingPage })),
);
const PricingPage = lazy(() =>
  import('@/pages/pricing.page').then((m) => ({ default: m.PricingPage })),
);
const RegisterPage = lazy(() =>
  import('@/pages/register.page').then((m) => ({ default: m.RegisterPage })),
);
const ReportsAgentPerformancePage = lazy(() =>
  import('@/pages/reports-agent-performance.page').then((m) => ({
    default: m.ReportsAgentPerformancePage,
  })),
);
const ReportsCsatPage = lazy(() =>
  import('@/pages/reports-csat.page').then((m) => ({ default: m.ReportsCsatPage })),
);
const ReportsNpsPage = lazy(() =>
  import('@/pages/reports-nps.page').then((m) => ({ default: m.ReportsNpsPage })),
);
const SettingsAgentCapacityPage = lazy(() =>
  import('@/pages/settings-agent-capacity.page').then((m) => ({
    default: m.SettingsAgentCapacityPage,
  })),
);
const SettingsAiAnalyticsPage = lazy(() =>
  import('@/pages/settings-ai-analytics.page').then((m) => ({
    default: m.SettingsAiAnalyticsPage,
  })),
);
const SettingsBrandingPage = lazy(() =>
  import('@/pages/settings-branding.page').then((m) => ({ default: m.SettingsBrandingPage })),
);
const SettingsCannedResponsesPage = lazy(() =>
  import('@/pages/settings-canned-responses.page').then((m) => ({
    default: m.SettingsCannedResponsesPage,
  })),
);
const SettingsChannelsRcsPage = lazy(() =>
  import('@/pages/settings-channels-rcs.page').then((m) => ({
    default: m.SettingsChannelsRcsPage,
  })),
);
const SettingsContactAttributesPage = lazy(() =>
  import('@/pages/settings-contact-attributes.page').then((m) => ({
    default: m.SettingsContactAttributesPage,
  })),
);
const SettingsEmailPage = lazy(() =>
  import('@/pages/settings-email.page').then((m) => ({ default: m.SettingsEmailPage })),
);
const SettingsEmailInboundPage = lazy(() =>
  import('@/pages/settings-email-inbound.page').then((m) => ({
    default: m.SettingsEmailInboundPage,
  })),
);
const SettingsFacebookPage = lazy(() =>
  import('@/pages/settings-facebook.page').then((m) => ({ default: m.SettingsFacebookPage })),
);
const SettingsInstagramPage = lazy(() =>
  import('@/pages/settings-instagram.page').then((m) => ({ default: m.SettingsInstagramPage })),
);
const SettingsSocialPostsPage = lazy(() =>
  import('@/pages/settings-social-posts.page').then((m) => ({
    default: m.SettingsSocialPostsPage,
  })),
);
const SettingsKnowledgeBasePage = lazy(() =>
  import('@/pages/settings-knowledge-base.page').then((m) => ({
    default: m.SettingsKnowledgeBasePage,
  })),
);
const SettingsKnowledgeBaseSourcesPage = lazy(() =>
  import('@/pages/settings-knowledge-base-sources.page').then((m) => ({
    default: m.KnowledgeBaseSourcesPage,
  })),
);
const SettingsLabelsPage = lazy(() =>
  import('@/pages/settings-labels.page').then((m) => ({ default: m.SettingsLabelsPage })),
);
const SettingsSlaPage = lazy(() =>
  import('@/pages/settings-sla.page').then((m) => ({ default: m.SlaSettingsDashboard })),
);
const SettingsHelpCenterPage = lazy(() =>
  import('@/pages/settings-help-center.page').then((m) => ({ default: m.SettingsHelpCenterPage })),
);
const SettingsMacrosPage = lazy(() =>
  import('@/pages/settings-macros.page').then((m) => ({ default: m.SettingsMacrosPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/settings.page').then((m) => ({ default: m.SettingsPage })),
);
const SettingsProfilePage = lazy(() =>
  import('@/pages/settings-profile.page').then((m) => ({ default: m.SettingsProfilePage })),
);
const SettingsPreChatFormsPage = lazy(() =>
  import('@/pages/settings-pre-chat-forms.page').then((m) => ({
    default: m.SettingsPreChatFormsPage,
  })),
);
const SettingsPrivacyPage = lazy(() =>
  import('@/pages/settings-privacy.page').then((m) => ({ default: m.SettingsPrivacyPage })),
);
const SettingsSamlPage = lazy(() =>
  import('@/pages/settings-saml.page').then((m) => ({ default: m.SettingsSamlPage })),
);
const SettingsSecurityPage = lazy(() =>
  import('@/pages/settings-security.page').then((m) => ({ default: m.SettingsSecurityPage })),
);
const SettingsSmsPage = lazy(() =>
  import('@/pages/settings-sms.page').then((m) => ({ default: m.SettingsSmsPage })),
);
const SettingsTelegramPage = lazy(() =>
  import('@/pages/settings-telegram.page').then((m) => ({ default: m.SettingsTelegramPage })),
);
const SettingsWaCallingPage = lazy(() =>
  import('@/pages/settings-wa-calling.page').then((m) => ({ default: m.SettingsWaCallingPage })),
);
const SettingsWaOfficialPage = lazy(() =>
  import('@/pages/settings-wa-official.page').then((m) => ({ default: m.SettingsWaOfficialPage })),
);
const SettingsWaUnofficialPage = lazy(() =>
  import('@/pages/settings-wa-unofficial.page').then((m) => ({
    default: m.SettingsWaUnofficialPage,
  })),
);
const SettingsWebhooksPage = lazy(() =>
  import('@/pages/settings-webhooks.page').then((m) => ({ default: m.SettingsWebhooksPage })),
);
const SettingsRolesPage = lazy(() =>
  import('@/pages/settings-roles.page').then((m) => ({ default: m.default })),
);
const TemplatesPage = lazy(() =>
  import('@/pages/templates.page').then((m) => ({ default: m.TemplatesPage })),
);
const ThreadPage = lazy(() =>
  import('@/pages/thread.page').then((m) => ({ default: m.ThreadPage })),
);
const WaFlowsPage = lazy(() =>
  import('@/pages/wa-flows.page').then((m) => ({ default: m.WaFlowsPage })),
);
const AdminCompliancePage = lazy(() =>
  import('@/pages/admin-compliance.page').then((m) => ({ default: m.AdminCompliancePage })),
);
const AdminRegionPage = lazy(() =>
  import('@/pages/admin-region.page').then((m) => ({ default: m.AdminRegionPage })),
);
const AnalyticsBuilderPage = lazy(() =>
  import('@/pages/analytics-builder.page').then((m) => ({ default: m.AnalyticsBuilderPage })),
);
const PhoneHealthPage = lazy(() =>
  import('@/pages/phone-health.page').then((m) => ({ default: m.PhoneHealthPage })),
);
const ResellerPage = lazy(() =>
  import('@/pages/reseller.page').then((m) => ({ default: m.ResellerPage })),
);
const DeveloperPage = lazy(() =>
  import('@/pages/developer.page').then((m) => ({ default: m.DeveloperPage })),
);
const AgentEmbedPage = lazy(() =>
  import('@/pages/embed/agent.page').then((m) => ({ default: m.AgentEmbedPage })),
);

// ── Root ──────────────────────────────────────────────────────────────────────
const rootRoute = createRootRoute();

// ── Public routes ─────────────────────────────────────────────────────────────
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  beforeLoad: () => {
    if (useAuthStore.getState().isAuthenticated) {
      throw redirect({ to: '/inbox' });
    }
  },
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: RegisterPage,
  beforeLoad: () => {
    if (useAuthStore.getState().isAuthenticated) {
      throw redirect({ to: '/inbox' });
    }
  },
});

const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pricing',
  component: PricingPage,
});

const checkoutSuccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/checkout/success',
  component: CheckoutSuccessPage,
});

const checkoutCancelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/checkout/cancel',
  component: CheckoutCancelPage,
});

// ── Protected layout ──────────────────────────────────────────────────────────
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  component: AppLayout,
  beforeLoad: () => {
    if (!useAuthStore.getState().isAuthenticated) {
      throw redirect({ to: '/login' });
    }

    // Redirect mobile users to /m if they land on the desktop root or inbox
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
    const { pathname } = window.location;
    if (isMobile && (pathname === '/' || pathname === '/inbox')) {
      throw redirect({ to: '/m' });
    }
  },
});

// ── Mobile layout ─────────────────────────────────────────────────────────────
const mobileLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_mobile',
  component: CompactDashboardShell,
  beforeLoad: () => {
    if (!useAuthStore.getState().isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
});

const mobileDashboardRoute = createRoute({
  getParentRoute: () => mobileLayoutRoute,
  path: '/m',
  component: MobileDashboardPage,
});

const mobileKpisRoute = createRoute({
  getParentRoute: () => mobileLayoutRoute,
  path: '/m/kpis',
  component: MobileDashboardPage, // Reusing components for MVP
});

const mobileAlertsRoute = createRoute({
  getParentRoute: () => mobileLayoutRoute,
  path: '/m/alerts',
  component: MobileDashboardPage,
});

const mobileWalletRoute = createRoute({
  getParentRoute: () => mobileLayoutRoute,
  path: '/m/wallet',
  component: MobileDashboardPage,
});

// ── Protected routes ──────────────────────────────────────────────────────────
const indexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/inbox' });
  },
  component: () => null,
});

const inboxRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/inbox',
  component: InboxV2Page,
});

const inboxThreadRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/inbox/$conversationId',
  component: ThreadPage,
});

const pipelineRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/pipeline',
  component: PipelinePage,
});

const segmentsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/segments',
  component: SegmentsPage,
});

const settingsWaOfficialRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/channels/whatsapp-official',
  component: SettingsWaOfficialPage,
});

const settingsWaUnofficialRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/channels/whatsapp-unofficial',
  component: SettingsWaUnofficialPage,
});

const settingsTelegramRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/channels/telegram',
  component: SettingsTelegramPage,
});

const settingsSmsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/channels/sms',
  component: SettingsSmsPage,
});

const settingsEmailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/channels/email',
  component: SettingsEmailPage,
});

const settingsEmailInboundRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/channels/email-inbound',
  component: SettingsEmailInboundPage,
});

const settingsFacebookRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/channels/facebook',
  component: SettingsFacebookPage,
});

const settingsInstagramRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/channels/instagram',
  component: SettingsInstagramPage,
});

const settingsSocialPostsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/channels/social-posts',
  component: SettingsSocialPostsPage,
});

const inboxesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/inboxes',
  component: InboxesPage,
});

const settingsCannedResponsesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/canned-responses',
  component: SettingsCannedResponsesPage,
});

const agentsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/agents',
  component: AgentsPage,
});

const settingsBrandingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/branding',
  component: SettingsBrandingPage,
});

const settingsAiAnalyticsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/ai-analytics',
  component: SettingsAiAnalyticsPage,
});

const settingsWebhooksRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/webhooks',
  component: SettingsWebhooksPage,
});

const settingsRolesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/roles',
  component: SettingsRolesPage,
});

const notificationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/notifications',
  component: NotificationsPage,
});

const onboardingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/onboarding',
  component: OnboardingPage,
});

const contactsImportRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/contacts/import',
  component: ContactsImportPage,
});

const apiKeysRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/api-keys',
  component: ApiKeysPage,
});

const auditLogsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/audit-logs',
  component: AuditLogsPage,
});

const contactsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/contacts',
  component: ContactsPage,
});

const campaignsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/campaigns',
  component: CampaignsPage,
});

const templatesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/templates',
  component: TemplatesPage,
});

const tagAutomationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/automations/tags',
  component: TagAutomationsPage,
});

const waFlowsListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/automation/wa-flows',
  component: WaFlowsPage,
});

const automationRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/automation',
  component: AutomationPage,
});

const botAnalyticsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/bot-analytics',
  component: BotAnalyticsPage,
});

const flowEditorRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/automation/$flowId/edit',
  component: FlowEditorPage,
});

// /flows is an alias for /automation (used by E2E tests and deep links)
const flowsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/flows',
  component: AutomationPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings',
  component: SettingsPage,
});

const settingsProfileRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/profile',
  component: SettingsProfilePage,
});

const analyticsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/analytics',
  component: AnalyticsPage,
});

const billingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/billing',
  component: BillingPage,
});

const marketplaceRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/marketplace',
  component: MarketplacePage,
});

const reportsCsatRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports/csat',
  component: ReportsCsatPage,
});

const reportsAgentPerformanceRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports/agent-performance',
  component: ReportsAgentPerformancePage,
});

const reportsNpsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports/nps',
  component: ReportsNpsPage,
});

const settingsPrivacyDsarRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/privacy/dsar',
  component: DsarManagementPage,
});

const settingsPrivacyRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/privacy',
  component: SettingsPrivacyPage,
});

const settingsAgentCapacityRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/agent-capacity',
  component: SettingsAgentCapacityPage,
});

const settingsSecurityRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/security',
  component: SettingsSecurityPage,
});

const settingsContactAttributesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/contact-attributes',
  component: SettingsContactAttributesPage,
});

const settingsPreChatFormsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/pre-chat-forms',
  component: SettingsPreChatFormsPage,
});

const liveViewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/live-view',
  component: LiveViewPage,
});

const settingsMacrosRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/macros',
  component: SettingsMacrosPage,
});

const settingsKnowledgeBaseRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/knowledge-base',
  component: SettingsKnowledgeBasePage,
});

const settingsKnowledgeBaseSourcesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/knowledge-base/sources',
  component: SettingsKnowledgeBaseSourcesPage,
});

const settingsChannelsRcsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/channels/rcs',
  component: SettingsChannelsRcsPage,
});

const settingsSamlRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/saml',
  component: SettingsSamlPage,
});

const settingsWaCallingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/channels/wa-calling',
  component: SettingsWaCallingPage,
});

const settingsLabelsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/labels',
  component: SettingsLabelsPage,
});

const settingsSlaRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/sla',
  component: SettingsSlaPage,
});

const settingsHelpCenterRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/help-center',
  component: SettingsHelpCenterPage,
});

const adminComplianceRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/admin/compliance',
  component: AdminCompliancePage,
});

const adminRegionRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/admin/region',
  component: AdminRegionPage,
});

const analyticsBuilderRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/analytics/builder',
  component: AnalyticsBuilderPage,
});

const phoneHealthRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/channels/phone-health',
  component: PhoneHealthPage,
});

const resellerRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reseller',
  component: ResellerPage,
});

const developerRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/developer',
  component: DeveloperPage,
});

// ── Embed routes (headless — sem sidebar/header, sem auth redirect) ───────────
// G-33.1: o JWT é recebido via query string ou postMessage 'init'
const embedAgentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/embed/agent',
  component: AgentEmbedPage,
});

// ── Route tree ────────────────────────────────────────────────────────────────
const routeTree = rootRoute.addChildren([
  loginRoute,
  registerRoute,
  pricingRoute,
  checkoutSuccessRoute,
  checkoutCancelRoute,
  embedAgentRoute,
  mobileLayoutRoute.addChildren([
    mobileDashboardRoute,
    mobileKpisRoute,
    mobileAlertsRoute,
    mobileWalletRoute,
  ]),
  appLayoutRoute.addChildren([
    indexRoute,
    inboxRoute,
    inboxThreadRoute,
    pipelineRoute,
    segmentsRoute,
    contactsRoute,
    tagAutomationsRoute,
    campaignsRoute,
    templatesRoute,
    automationRoute,
    botAnalyticsRoute,
    waFlowsListRoute,
    flowEditorRoute,
    flowsRoute,
    settingsRoute,
    settingsProfileRoute,
    settingsWaOfficialRoute,
    settingsWaUnofficialRoute,
    settingsTelegramRoute,
    settingsSmsRoute,
    settingsEmailRoute,
    settingsEmailInboundRoute,
    settingsFacebookRoute,
    settingsInstagramRoute,
    settingsSocialPostsRoute,
    inboxesRoute,
    settingsCannedResponsesRoute,
    agentsRoute,
    analyticsRoute,
    billingRoute,
    marketplaceRoute,
    settingsBrandingRoute,
    settingsAiAnalyticsRoute,
    settingsWebhooksRoute,
    notificationsRoute,
    onboardingRoute,
    contactsImportRoute,
    apiKeysRoute,
    auditLogsRoute,
    reportsCsatRoute,
    reportsAgentPerformanceRoute,
    reportsNpsRoute,
    settingsPrivacyDsarRoute,
    settingsPrivacyRoute,
    settingsSecurityRoute,
    settingsAgentCapacityRoute,
    settingsContactAttributesRoute,
    settingsPreChatFormsRoute,
    liveViewRoute,
    settingsMacrosRoute,
    settingsKnowledgeBaseRoute,
    settingsKnowledgeBaseSourcesRoute,
    settingsChannelsRcsRoute,
    settingsSamlRoute,
    settingsWaCallingRoute,
    settingsLabelsRoute,
    settingsRolesRoute,
    settingsSlaRoute,
    settingsHelpCenterRoute,
    adminComplianceRoute,
    adminRegionRoute,
    analyticsBuilderRoute,
    phoneHealthRoute,
    resellerRoute,
    developerRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPendingMs: 200,
  defaultPendingComponent: () =>
    createElement(
      'div',
      { className: 'flex h-full w-full items-center justify-center p-12' },
      createElement(Loader2, { className: 'h-6 w-6 animate-spin text-primary' }),
    ),
});

// Type registration for TanStack Router
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
